import { Col, Modal, Row, message } from 'antd';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import embargoBankAccountInfoRequestApi from '../../../apis/EmbargoBankAccountInfoRequestApi';
import { Bank, CouncilBank, EmbargoBankAccountInfoRequest } from '../../../models/Entities';
import alertService from '../../../services/AlertService';
import TextComponent from '../../TextComponent/TextComponent';
import styles from './EmbargoBankAccountInfoRequestModal.module.scss';
import councilBankApi from '../../../apis/CouncilBankApi';
import bankApi from '../../../apis/BankApi';

const EmbargoBankAccountInfoRequestModal = (props: Props) => {
    const { councilId, receivableIds } = props;

    /*** HOOKS ***/

    const intl = useIntl();
    const [text, setText] = useState<string>();
    const [loading, setLoading] = useState<'loading' | 'saving'>();
    const [councilBanks, setCouncilBanks] = useState<CouncilBank[]>([]);
    const [banks, setBanks] = useState<Bank[]>([]);

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');

                // get draft of embargo bank account info request file (c63)
                const embargoBankAccountInfoRequest: EmbargoBankAccountInfoRequest = { councilId, receivableIds };
                const file = await embargoBankAccountInfoRequestApi.downloadDraft(embargoBankAccountInfoRequest);
                const text = await file.text();
                setText(text);

                // list council banks
                const councilBanksPage = await councilBankApi.list(0, 1000, 'bankName', true, councilId, 'ENABLED');
                setCouncilBanks(councilBanksPage.content);

                // list banks (online)
                const banksPage = await bankApi.list(0, 1000, 'name', true, 'ENABLED', undefined, true);
                setBanks(banksPage.content);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [councilId, intl, receivableIds]);

    /*** METHODS ***/

    //save
    const save = async () => {
        try {
            setLoading('saving');
            const embargoBankAccountInfoRequest: EmbargoBankAccountInfoRequest = { councilId, receivableIds };
            await embargoBankAccountInfoRequestApi.create(embargoBankAccountInfoRequest);
            await props.onSave();
            message.success(intl.formatMessage({ id: 'status.saved' }));
        } catch (error: any) {
            !error.errorFields && alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    // cancel
    const cancel = async () => {
        props.onCancel();
    };

    /*** VISUAL ***/

    return (
        <Modal
            title={<FormattedMessage id="receivables.actions.embargoBankAccountInfoRequest.modal.title" />}
            open={true}
            onCancel={cancel}
            onOk={save}
            okText={<FormattedMessage id="receivables.actions.embargoBankAccountInfoRequest.modal.send" tagName="span" />}
            okButtonProps={{ loading: loading === 'saving', size: 'large' }}
            cancelButtonProps={{ disabled: loading === 'saving', size: 'large' }}
            width={1000}
        >
            <Row gutter={[0, 0]} className={styles.container}>
                <Col span={24} className={styles.file}>
                    <TextComponent text={text} />
                </Col>
                <Col span={24}>
                    <FormattedMessage id="receivables.actions.embargoBankAccountInfoRequest.modal.councilBanks" />
                </Col>
                <Col span={24}>
                    <ul>
                        <li>{councilBanks.map((councilBank) => councilBank.bank?.name).join(', ')}</li>
                    </ul>
                </Col>
                <Col span={24}>
                    <FormattedMessage id="receivables.actions.embargoBankAccountInfoRequest.modal.banks" />
                </Col>
                <Col span={24}>
                    <ul>
                        <li>{banks.map((bank) => bank.name).join(', ')}</li>
                    </ul>
                </Col>
            </Row>
        </Modal>
    );
};
export default EmbargoBankAccountInfoRequestModal;

interface Props {
    receivableIds: number[];
    councilId: number;
    onSave: () => Promise<void>;
    onCancel: () => void;
}
